import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { GridProps } from '@mui/material'
import { type Nil, TextWithTooltip, Utils } from '@pbt/pbt-ui-components'

import ClientWarningChip from '~/components/common/ClientWarningChip'
import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import Link from '~/components/common/link/Link'
import useGetPreferredContactMethod from '~/components/dashboard/clients/details/new-client-and-patient/useGetPreferredContactMethod'
import { ClientPreferencesChip } from '~/components/dashboard/clients/preferences/ClientPreferencesChip'
import LinkedChewyAccountContainer, {
  KyriosAccountType,
} from '~/components/dashboard/link-chewy-account/LinkedChewyAccountContainer'
import { fetchClient } from '~/store/actions/clients'
import { getUser } from '~/store/reducers/users'
import type { TimetableEvent } from '~/types'

import EntityDisplay from './EntityDisplay'

interface ClientDisplayProps extends GridProps {
  appointment?: TimetableEvent
  clientId: string | Nil
  isLink?: boolean
  loadClient?: boolean
}

/**
 * Displays client information in a consistent layout:
 * name > chips
 *
 * Note that this version does not load consent form data
 */
const ClientDisplay = ({
  appointment,
  clientId,
  isLink = false,
  loadClient = false,
  ...props
}: ClientDisplayProps) => {
  const client = useSelector(getUser(clientId))
  const dispatch = useDispatch()

  const preferredPhoneContactMethod = useGetPreferredContactMethod({
    client,
    phoneOnly: true,
  })

  useEffect(() => {
    if (loadClient && clientId) {
      dispatch(fetchClient({ clientId }))
    }
  }, [loadClient, clientId])

  if (!client) {
    return null
  }

  const nameContent = (
    <TextWithTooltip
      noIcon
      strong
      TooltipProps={{ tooltipPlacement: 'bottom-start' }}
      tooltipText={`${preferredPhoneContactMethod?.value ?? ''}\n${
        client?.email ?? ''
      }`}
      underline={isLink}
    >
      {Utils.getPersonString(client)}
    </TextWithTooltip>
  )

  const name = isLink ? (
    <Link id="client-display-link" target="_blank" to={`/client/${clientId}`}>
      {nameContent}
    </Link>
  ) : (
    nameContent
  )

  const chips = (
    <>
      <ClientWarningChip clientId={clientId} />
      <ClientPreferencesChip appointment={appointment} clientId={clientId} />
      <LinkedChewyAccountContainer
        accountType={KyriosAccountType.CLIENT}
        clientId={clientId}
      />
      <BusinessShareIcon businessIds={client?.clientInContextBusinesses} />
    </>
  )

  return <EntityDisplay chips={chips} name={name} {...props} />
}

export default ClientDisplay
